/**
 * Cognito Authentication Client
 *
 * Handles signup, login, MFA, token management, and session persistence.
 * Uses the Cognito hosted UI SDK pattern (no Amplify dependency).
 *
 * Reliability fixes:
 * - localStorage (persists across tab close, iOS PWA background)
 * - 5-minute token refresh buffer
 * - ExpiresIn validation (fallback 3600s)
 * - Retry with backoff on network failures
 * - Request timeouts (15s)
 * - PKCE verifier in localStorage (survives browser redirect)
 * - OAuth state parameter for CSRF protection
 */

import { recordLocalPasskey, getLocalPasskeyMap } from './passkey-device';

const COGNITO_REGION = import.meta.env.VITE_COGNITO_REGION || 'us-east-1';
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '';
const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN || '';
const REDIRECT_URI = import.meta.env.VITE_OAUTH_REDIRECT_URI || window.location.origin + '/oauth/callback';

const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 min before expiry
const REQUEST_TIMEOUT_MS = 15000; // 15s timeout for Cognito calls
const MAX_RETRIES = 2;

interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
}

interface AuthUser {
  cognitoId: string;
  email: string;
  emailVerified: boolean;
}

// ============================================================
// Token Storage — localStorage for persistence
// ============================================================
let currentTokens: AuthTokens | null = null;

function saveTokens(tokens: AuthTokens) {
  currentTokens = tokens;
  try {
    localStorage.setItem('nexus_tokens', JSON.stringify(tokens));
  } catch { /* privacy mode */ }
  // Best-effort: also pin the refresh token in an HttpOnly cookie via the
  // server. Survives iOS Safari ITP eviction of localStorage. Failure here
  // is not fatal - localStorage path is the primary; cookie is a fallback.
  if (tokens.refreshToken) {
    void pinRefreshCookie(tokens.refreshToken);
  }
}

function loadTokens(): AuthTokens | null {
  if (currentTokens) return currentTokens;
  try {
    const stored = localStorage.getItem('nexus_tokens');
    if (stored) {
      currentTokens = JSON.parse(stored);
      return currentTokens;
    }
  } catch { /* privacy mode */ }
  return null;
}

function clearTokens() {
  currentTokens = null;
  try {
    localStorage.removeItem('nexus_tokens');
    // Also clean up legacy sessionStorage tokens
    sessionStorage.removeItem('nexus_tokens');
  } catch { /* privacy mode */ }
  // Best-effort: tell the server to clear the refresh cookie too.
  void clearRefreshCookie();
}

// ============================================================
// HttpOnly refresh-token cookie path (iOS Safari ITP defense)
// ============================================================
//
// Same-origin POSTs to /v1/auth/store-refresh, /v1/auth/refresh, /v1/auth/logout.
// Browser auto-includes credentials on same-origin (no `credentials: 'include'`
// needed). The cookie is HttpOnly so JS never reads it directly.
const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

async function pinRefreshCookie(refreshToken: string): Promise<void> {
  if (!API_BASE) return;
  try {
    await fetch(`${API_BASE}/auth/store-refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
      credentials: 'same-origin',
    });
  } catch { /* non-fatal */ }
}

async function clearRefreshCookie(): Promise<void> {
  if (!API_BASE) return;
  try {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'same-origin',
    });
  } catch { /* non-fatal */ }
}

/**
 * Fall back to the HttpOnly refresh cookie when localStorage is empty
 * (iOS Safari ITP cleared it, or this is a fresh PWA install).
 * Returns fresh AuthTokens or null if the cookie is missing/expired.
 */
async function refreshFromCookie(): Promise<AuthTokens | null> {
  if (!API_BASE) return null;
  try {
    const resp = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'same-origin',
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    if (!data.idToken || !data.accessToken) return null;
    const tokens: AuthTokens = {
      accessToken: data.accessToken,
      idToken: data.idToken,
      // Server-side keeps the refresh token in the HttpOnly cookie; we
      // never see it client-side. Subsequent refreshes go via the
      // cookie path until the user signs in fresh and gets the
      // localStorage refresh restored.
      refreshToken: '',
      expiresAt: Date.now() + expiresInToMs(data.expiresIn),
    };
    currentTokens = tokens;
    try { localStorage.setItem('nexus_tokens', JSON.stringify(tokens)); } catch { /* */ }
    return tokens;
  } catch {
    return null;
  }
}

/** Safe ExpiresIn extraction — returns milliseconds to add to Date.now() */
function expiresInToMs(expiresIn: unknown): number {
  const val = typeof expiresIn === 'number' ? expiresIn : parseInt(String(expiresIn), 10);
  if (!val || isNaN(val) || val <= 0) return 3600 * 1000; // Default 1 hour
  return val * 1000;
}

// ============================================================
// Fetch with timeout + retry
// ============================================================
async function fetchWithTimeout(url: string, options: RequestInit, timeoutMs = REQUEST_TIMEOUT_MS): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    return response;
  } finally {
    clearTimeout(timer);
  }
}

async function cognitoFetch(target: string, body: Record<string, unknown>, retries = MAX_RETRIES): Promise<Response> {
  const url = `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/`;
  const options: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  };

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fetchWithTimeout(url, options);
    } catch (err) {
      const isLast = attempt === retries;
      const isAbort = err instanceof DOMException && err.name === 'AbortError';
      const isNetwork = err instanceof TypeError;

      if (isLast) throw err;
      if (!isAbort && !isNetwork) throw err; // Don't retry non-network errors

      // Exponential backoff: 1s, 2s
      await new Promise(r => setTimeout(r, (attempt + 1) * 1000));
    }
  }

  throw new Error('Request failed after retries');
}

// ============================================================
// Token Refresh
// ============================================================
let refreshPromise: Promise<AuthTokens | null> | null = null;

/** Get the current ID token for API calls, refreshing if near expiry. */
export async function getAccessToken(forceRefresh = false): Promise<string | null> {
  const tokens = loadTokens();

  // No tokens locally - try the HttpOnly cookie path (iOS Safari ITP
  // cleared localStorage, or fresh PWA install on a device that previously
  // signed in via the same-origin browser).
  if (!tokens) {
    const fromCookie = await refreshFromCookie();
    return fromCookie ? fromCookie.idToken : null;
  }

  // Refresh if forced, expired, or within 5-minute buffer
  if (forceRefresh || Date.now() > tokens.expiresAt - TOKEN_REFRESH_BUFFER_MS) {
    // Deduplicate concurrent refresh calls
    if (!refreshPromise) {
      refreshPromise = (async () => {
        // Try localStorage refresh first (no network round-trip if Cognito
        // accepts the token).
        if (tokens.refreshToken) {
          const r = await refreshSession(tokens.refreshToken);
          if (r) return r;
        }
        // localStorage refresh failed or absent - try the HttpOnly cookie.
        return await refreshFromCookie();
      })().finally(() => { refreshPromise = null; });
    }
    const refreshed = await refreshPromise;
    if (refreshed) return refreshed.idToken;
    clearTokens();
    return null;
  }

  return tokens.idToken;
}

/**
 * Get the raw ACCESS token (not the ID token that getAccessToken returns).
 * WebAuthn self-service ops (StartWebAuthnRegistration etc.) require the access
 * token, which carries the implicit `aws.cognito.signin.user.admin` scope.
 * Reuses getAccessToken() to refresh+persist, then reads the access token from
 * the freshly-saved tokens.
 */
export async function getAccessTokenRaw(forceRefresh = false): Promise<string | null> {
  await getAccessToken(forceRefresh); // refresh + persist if near expiry
  // Return the access token only if it's actually present + non-empty. The
  // cookie-refresh path can repopulate tokens without a usable access token;
  // returning null lets registerPasskey show "sign in again" rather than
  // calling StartWebAuthnRegistration with an empty token (NotAuthorized).
  const t = loadTokens();
  return t && t.accessToken ? t.accessToken : null;
}

/** Check if user is authenticated */
export function isAuthenticated(): boolean {
  const tokens = loadTokens();
  if (!tokens) return false;
  // Use refresh buffer — if within 5 min of expiry, still "authenticated"
  // but getAccessToken() will refresh. Only mark unauthenticated if truly expired.
  return Date.now() < tokens.expiresAt;
}

// ============================================================
// Sign Up
// ============================================================
export async function signUp(email: string, password: string, fullName: string): Promise<{ success: boolean; message: string }> {
  try {
    const response = await cognitoFetch('SignUp', {
      ClientId: CLIENT_ID,
      Username: email,
      Password: password,
      UserAttributes: [
        { Name: 'email', Value: email },
        { Name: 'name', Value: fullName },
      ],
    });

    if (!response.ok) {
      const err = await response.json();
      return { success: false, message: err.message || 'Sign up failed' };
    }

    return { success: true, message: 'Account created. Check your email for verification code.' };
  } catch (err) {
    console.error('SignUp error:', err);
    const msg = err instanceof DOMException && err.name === 'AbortError'
      ? 'Request timed out. Please try again.'
      : 'Network error during sign up. Please check your connection.';
    return { success: false, message: msg };
  }
}

// ============================================================
// Sign In
// ============================================================
export async function signIn(email: string, password: string): Promise<{
  success: boolean;
  message: string;
  mfaRequired?: boolean;
  mfaSetupRequired?: boolean;
  newPasswordRequired?: boolean;
  session?: string;
}> {
  try {
    const response = await cognitoFetch('InitiateAuth', {
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: CLIENT_ID,
      AuthParameters: { USERNAME: email, PASSWORD: password },
    });

    const data = await response.json();

    if (!response.ok) {
      return { success: false, message: data.message || `Sign in failed (${response.status})` };
    }

    if (data.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
      return { success: false, mfaRequired: true, session: data.Session, message: 'MFA required' };
    }

    if (data.ChallengeName === 'MFA_SETUP') {
      return { success: false, mfaSetupRequired: true, session: data.Session, message: 'MFA setup required' };
    }

    // Admin-created / admin-reset accounts (AdminSetUserPassword --no-permanent)
    // sign in with a temporary password and land in FORCE_CHANGE_PASSWORD state.
    // Cognito answers with NEW_PASSWORD_REQUIRED; the user must set a permanent
    // password via RespondToAuthChallenge (completeNewPassword below) before any
    // MFA challenge fires. Pre-fix this fell through to the generic error and
    // dead-ended the user (2026-07-03 nexus-prod cutover finding).
    if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
      return { success: false, newPasswordRequired: true, session: data.Session, message: 'A new password is required' };
    }

    if (data.AuthenticationResult) {
      const tokens: AuthTokens = {
        accessToken: data.AuthenticationResult.AccessToken,
        idToken: data.AuthenticationResult.IdToken,
        refreshToken: data.AuthenticationResult.RefreshToken,
        expiresAt: Date.now() + expiresInToMs(data.AuthenticationResult.ExpiresIn),
      };
      saveTokens(tokens);
      return { success: true, message: 'Signed in' };
    }

    return { success: false, message: data.message || 'Unexpected response from Cognito' };
  } catch (err) {
    console.error('SignIn error:', err);
    const msg = err instanceof DOMException && err.name === 'AbortError'
      ? 'Request timed out. Please try again.'
      : 'Network error during sign in. Please check your connection.';
    return { success: false, message: msg };
  }
}

// ============================================================
// Demo session — server-minted (NS13 Piece A)
// ============================================================
//
// Mints a viewer session for the shared Helix demo tenant WITHOUT the
// browser ever holding the demo password or TOTP secret. POSTs to the
// public /demo/session endpoint, which completes the SOFTWARE_TOKEN_MFA
// challenge server-side (cognito-proxy) and returns JWTs. Replaces the old
// hardcoded-password sign-in that broke when the pool went
// MfaConfiguration=ON (and that shipped the demo password in the JS bundle).
export async function startDemoSession(): Promise<{ success: boolean; message: string }> {
  if (!API_BASE) return { success: false, message: 'API base URL not configured' };
  try {
    const response = await fetch(`${API_BASE}/demo/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.tokens?.idToken) {
      return {
        success: false,
        message: data.message || 'Demo is temporarily unavailable. Please try again in a moment.',
      };
    }
    const t = data.tokens;
    const tokens: AuthTokens = {
      accessToken: t.accessToken,
      idToken: t.idToken,
      refreshToken: t.refreshToken,
      expiresAt: Date.now() + expiresInToMs(t.expiresIn),
    };
    saveTokens(tokens);
    return { success: true, message: 'Demo session started' };
  } catch (err) {
    console.error('Demo session error:', err);
    return { success: false, message: 'Network error starting demo. Please try again.' };
  }
}

// ============================================================
// MFA Verify
// ============================================================
export async function verifyMFA(session: string, code: string, email: string): Promise<{ success: boolean; message: string }> {
  try {
    const response = await cognitoFetch('RespondToAuthChallenge', {
      ChallengeName: 'SOFTWARE_TOKEN_MFA',
      ClientId: CLIENT_ID,
      Session: session,
      ChallengeResponses: { USERNAME: email, SOFTWARE_TOKEN_MFA_CODE: code },
    });

    const data = await response.json();

    if (data.AuthenticationResult) {
      const tokens: AuthTokens = {
        accessToken: data.AuthenticationResult.AccessToken,
        idToken: data.AuthenticationResult.IdToken,
        refreshToken: data.AuthenticationResult.RefreshToken,
        expiresAt: Date.now() + expiresInToMs(data.AuthenticationResult.ExpiresIn),
      };
      saveTokens(tokens);
      return { success: true, message: 'MFA verified' };
    }

    return { success: false, message: data.message || 'Invalid MFA code' };
  } catch (err) {
    console.error('MFA verify error:', err);
    return { success: false, message: 'MFA verification failed. Please try again.' };
  }
}

// ============================================================
// NEW_PASSWORD_REQUIRED — set a permanent password (temp-password accounts)
// ============================================================
//
// Completes the FORCE_CHANGE_PASSWORD flow for admin-created / admin-reset
// accounts. Mirrors verifyMFA/completeMFASetup: RespondToAuthChallenge with
// { USERNAME, NEW_PASSWORD } + the Session from signIn's NEW_PASSWORD_REQUIRED
// response. The pool declares no required standard attributes (nexus-stack.ts
// UserPool), so no userAttributes.* entries are needed.
//
// IMPORTANT: the pool is MfaConfiguration=ON, so a successful password change
// is usually NOT terminal — Cognito immediately issues the next challenge with
// a fresh Session: MFA_SETUP for accounts without TOTP (the normal case for a
// fresh admin-created account) or SOFTWARE_TOKEN_MFA when TOTP is enrolled.
// Callers must chain into the same MFA handling as signIn. A direct
// AuthenticationResult is handled defensively.
export async function completeNewPassword(session: string, email: string, newPassword: string): Promise<{
  success: boolean;
  message: string;
  mfaRequired?: boolean;
  mfaSetupRequired?: boolean;
  sessionExpired?: boolean;
  session?: string;
}> {
  try {
    const response = await cognitoFetch('RespondToAuthChallenge', {
      ChallengeName: 'NEW_PASSWORD_REQUIRED',
      ClientId: CLIENT_ID,
      Session: session,
      ChallengeResponses: { USERNAME: email, NEW_PASSWORD: newPassword },
    });

    const data = await response.json();

    if (!response.ok) {
      const errType = String(data.__type || '');
      if (errType.includes('InvalidPassword')) {
        return { success: false, message: data.message || 'That password does not meet the requirements. Use at least 12 characters with upper/lowercase letters, a number, and a symbol.' };
      }
      // The challenge Session from InitiateAuth is short-lived (~3 min).
      // NotAuthorized here almost always means it expired while the user was
      // on the set-password screen — send them back to sign in again with the
      // temporary password (typed flag so the UI can branch cleanly).
      if (errType.includes('NotAuthorized') || errType.includes('ExpiredCode')) {
        return { success: false, sessionExpired: true, message: 'Your session expired. Please sign in again with the temporary password.' };
      }
      return { success: false, message: data.message || 'Could not set the new password. Please try again.' };
    }

    if (data.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
      return { success: false, mfaRequired: true, session: data.Session, message: 'MFA required' };
    }
    if (data.ChallengeName === 'MFA_SETUP') {
      return { success: false, mfaSetupRequired: true, session: data.Session, message: 'MFA setup required' };
    }

    if (data.AuthenticationResult) {
      const tokens: AuthTokens = {
        accessToken: data.AuthenticationResult.AccessToken,
        idToken: data.AuthenticationResult.IdToken,
        refreshToken: data.AuthenticationResult.RefreshToken,
        expiresAt: Date.now() + expiresInToMs(data.AuthenticationResult.ExpiresIn),
      };
      saveTokens(tokens);
      return { success: true, message: 'Password updated. You are signed in.' };
    }

    return { success: false, message: data.message || 'Unexpected response from Cognito' };
  } catch (err) {
    console.error('completeNewPassword error:', err);
    const msg = err instanceof DOMException && err.name === 'AbortError'
      ? 'Request timed out. Please try again.'
      : 'Network error while setting the new password. Please check your connection.';
    return { success: false, message: msg };
  }
}

// ============================================================
// Passkeys / WebAuthn (Piece B, B-2/B-3) — Face ID / Touch ID / Windows Hello
// ============================================================
//
// Browser-direct, exactly like the TOTP self-service calls above: the Cognito
// user-pool API (cognito-idp.<region>.amazonaws.com) is called via cognitoFetch
// with the X-Amz-Target header. No backend Lambda / API route is needed — the
// ceremony ops are the same access-token-authorized + Session-based shapes the
// rest of this file already uses.
//
//   Register (signed-in user adds a passkey):
//     StartWebAuthnRegistration { AccessToken } -> { CredentialCreationOptions }
//     -> navigator.credentials.create(...) -> CompleteWebAuthnRegistration
//        { AccessToken, Credential: RegistrationResponseJSON }
//   Sign in (passwordless, passkey satisfies the required-MFA pool in one step
//   because the pool FactorConfiguration = MULTI_FACTOR_WITH_USER_VERIFICATION):
//     InitiateAuth AuthFlow=USER_AUTH, PREFERRED_CHALLENGE=WEB_AUTHN
//     -> WEB_AUTHN challenge w/ CREDENTIAL_REQUEST_OPTIONS (a JSON string)
//     -> navigator.credentials.get(...) -> RespondToAuthChallenge
//        ChallengeResponses { USERNAME, CREDENTIAL: JSON.stringify(assertion) }
//
// WebAuthn passes binary fields (challenge, credential id, etc.) as base64url in
// JSON; the browser APIs need ArrayBuffers. We convert manually (works on every
// passkey-capable browser, no reliance on the newer parse*FromJSON()/toJSON()).

/**
 * True if this browser exposes the WebAuthn *API* (PublicKeyCredential +
 * navigator.credentials.create). This is the cheap, synchronous gate — it does
 * NOT prove the device has a usable platform authenticator (a desktop with no
 * fingerprint/face/PIN still returns true here). Use it to gate the cross-device
 * passkey fallback (QR to phone), which works regardless of local hardware. To
 * decide whether to OFFER a local biometric set-up, use the async
 * isPlatformAuthenticatorAvailable() instead.
 */
export function isPasskeySupported(): boolean {
  return typeof window !== 'undefined'
    && typeof window.PublicKeyCredential === 'function'
    && typeof navigator !== 'undefined'
    && !!navigator.credentials
    && typeof navigator.credentials.create === 'function';
}

/**
 * True only if THIS device has a user-verifying *platform* authenticator
 * (Windows Hello / Touch ID / Face ID / built-in fingerprint, configured and
 * usable). isPasskeySupported() only confirms the API exists — that's exactly
 * the "advertise Windows Hello on a PC that lacks it" bug. Async because the
 * WebAuthn UVPAA probe returns a Promise. Fail-closed: a missing probe, a
 * throw/reject, or an unsupported browser all return false, because we only
 * OFFER to set up a local passkey when we positively know one can be created.
 *
 * HARD LIMIT (drives getPasskeyDeviceNote): UVPAA returns ONE boolean. WebAuthn
 * exposes no way to tell which modality (face vs fingerprint vs PIN) a platform
 * authenticator supports, so a web app cannot detect "no infrared camera." We
 * state that limit in copy rather than pretend to detect it.
 */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (!isPasskeySupported()) return false;
  try {
    const probe = window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable;
    if (typeof probe !== 'function') return false;
    // Coerce to a strict boolean — fail closed against any non-conforming probe.
    return (await probe.call(window.PublicKeyCredential)) === true;
  } catch {
    return false;
  }
}

/**
 * Device-aware name for the local biometric/passkey, so every surface (Login
 * button, Settings card, success toasts) says the RIGHT thing for the device:
 * iPhone/iPad -> Face ID, Mac -> Touch ID, Windows -> Windows Hello,
 * Android -> fingerprint, else -> a passkey. Single source of truth — imported
 * by Login.tsx and settings/Security.tsx (do NOT re-define locally).
 */
export function getBiometricLabel(): string {
  if (typeof navigator === 'undefined') return 'a passkey';
  const ua = navigator.userAgent || '';
  const plat = ((navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform
    || navigator.platform || '').toLowerCase();
  if (/ipad|iphone|ipod/i.test(ua)) return 'Face ID';
  if (/macintosh|mac os x/i.test(ua) || plat.includes('mac')) return 'Touch ID';
  if (/windows/i.test(ua) || plat.includes('win')) return 'Windows Hello';
  if (/android/i.test(ua)) return 'your fingerprint';
  return 'a passkey';
}

/**
 * Honest, device-specific note shown beside every "set up a passkey" surface.
 * Returns null when there's nothing worth clarifying.
 *
 * Windows is the one platform that needs it: "Windows Hello" umbrellas face,
 * fingerprint, AND PIN, and face quietly requires an infrared camera. A web app
 * cannot detect a missing camera (see isPlatformAuthenticatorAvailable), so we
 * state the rule up front — otherwise users hunt for a Face option their PC
 * can't show (the operator lost weeks to exactly this). iOS/Mac/Android name a
 * single modality on the button itself, so no extra note. Single source of
 * truth — imported by Login.tsx, settings/Security.tsx, PasskeyEnrollPrompt.tsx.
 */
export function getPasskeyDeviceNote(): { heading: string; body: string } | null {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';
  const plat = ((navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform
    || navigator.platform || '').toLowerCase();
  // Apple/Android resolve to a single named modality — no ambiguity to explain.
  if (/ipad|iphone|ipod/i.test(ua)) return null;
  if (/macintosh|mac os x/i.test(ua) || plat.includes('mac')) return null;
  if (/android/i.test(ua)) return null;
  if (/windows/i.test(ua) || plat.includes('win')) {
    return {
      heading: 'Face recognition requires an infrared camera',
      body: 'If your device doesn’t have one, Windows Hello offers fingerprint or PIN instead — both are equally secure.',
    };
  }
  return null;
}

/**
 * True once the user has enrolled a *platform* passkey on THIS browser/device
 * (set after a successful registerPasskey). Lets the UI stop showing the
 * "set up <biometric> on this device" nudge once it's done. Per-device by design
 * (localStorage), since a passkey is bound to the device it was created on.
 */
const PLATFORM_PASSKEY_FLAG = 'nexus_has_platform_passkey';
export function hasEnrolledPlatformPasskey(): boolean {
  try { return localStorage.getItem(PLATFORM_PASSKEY_FLAG) === '1'; } catch { return false; }
}

function b64urlToBuf(value: string): ArrayBuffer {
  const pad = value.length % 4 === 0 ? '' : '='.repeat(4 - (value.length % 4));
  const b64 = value.replace(/-/g, '+').replace(/_/g, '/') + pad;
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

function bufToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

interface JsonCredDescriptor { id: string; type: string; transports?: string[] }

/**
 * PublicKeyCredentialCreationOptionsJSON -> CredentialCreationOptions (buffers).
 *
 * We OVERRIDE Cognito's `authenticatorSelection` to FORCE a *platform* credential
 * (Windows Hello / Touch ID / Face ID bound to THIS device). Cognito leaves
 * `authenticatorAttachment` unset, which makes Windows offer the cross-device QR
 * path and produces a *phone* passkey that can never engage Windows Hello at
 * sign-in. Forcing `platform` + a discoverable (resident) credential + user
 * verification makes the enrolled passkey the device's own biometric, which is
 * exactly what the device-aware "Sign in with Windows Hello / Face ID" button
 * promises. `hints: ['client-device']` (WebAuthn L3) tells the browser to lead
 * with this device, not a phone. Exported for unit testing.
 */
export function decodeCreationOptions(json: Record<string, unknown>): CredentialCreationOptions {
  const o = json as Record<string, any>;
  const sel = (o.authenticatorSelection || {}) as Record<string, unknown>;
  const pk = {
    ...o,
    challenge: b64urlToBuf(o.challenge),
    user: { ...o.user, id: b64urlToBuf(o.user.id) },
    excludeCredentials: (o.excludeCredentials || []).map((c: JsonCredDescriptor) => ({
      ...c,
      id: b64urlToBuf(c.id),
    })),
    authenticatorSelection: {
      ...sel,
      authenticatorAttachment: 'platform',
      residentKey: (sel.residentKey as string) || 'required',
      requireResidentKey: true,
      userVerification: 'required',
    },
    hints: ['client-device'],
  } as unknown as PublicKeyCredentialCreationOptions;
  return { publicKey: pk };
}

/**
 * PublicKeyCredentialRequestOptionsJSON -> CredentialRequestOptions (buffers).
 *
 * Adds `hints: ['client-device']` (WebAuthn L3) so the browser/OS LEADS with the
 * local platform authenticator (Windows Hello / Touch ID / Face ID) instead of
 * the cross-device QR. This is a *preference*, not a filter — a phone passkey
 * still works via "use another device" — but once a Windows Hello passkey exists
 * on this machine, sign-in engages it directly with no QR. Exported for testing.
 */
export function decodeRequestOptions(json: Record<string, unknown>): CredentialRequestOptions {
  const o = json as Record<string, any>;
  const pk = {
    ...o,
    challenge: b64urlToBuf(o.challenge),
    allowCredentials: (o.allowCredentials || []).map((c: JsonCredDescriptor) => ({
      ...c,
      id: b64urlToBuf(c.id),
    })),
    hints: ['client-device'],
    userVerification: o.userVerification || 'required',
  } as unknown as PublicKeyCredentialRequestOptions;
  return { publicKey: pk };
}

/** PublicKeyCredential (attestation) -> RegistrationResponseJSON. */
function encodeRegistration(cred: PublicKeyCredential): Record<string, unknown> {
  const resp = cred.response as AuthenticatorAttestationResponse;
  const transports = typeof resp.getTransports === 'function' ? resp.getTransports() : undefined;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bufToB64url(resp.clientDataJSON),
      attestationObject: bufToB64url(resp.attestationObject),
      ...(transports && transports.length ? { transports } : {}),
    },
  };
}

/** PublicKeyCredential (assertion) -> AuthenticationResponseJSON. */
function encodeAssertion(cred: PublicKeyCredential): Record<string, unknown> {
  const resp = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bufToB64url(resp.clientDataJSON),
      authenticatorData: bufToB64url(resp.authenticatorData),
      signature: bufToB64url(resp.signature),
      ...(resp.userHandle ? { userHandle: bufToB64url(resp.userHandle) } : {}),
    },
  };
}

/**
 * Register a passkey for the currently signed-in user (B-3 / B-4).
 * Requires a valid access token (user has signed in at least once — an AWS
 * prerequisite). Returns success/false + a user-facing message.
 */
export async function registerPasskey(friendlyName?: string): Promise<{ success: boolean; message: string; cancelled?: boolean }> {
  if (!isPasskeySupported()) {
    return { success: false, message: 'This device or browser does not support passkeys.' };
  }
  const accessToken = await getAccessTokenRaw();
  if (!accessToken) {
    return { success: false, message: 'Please sign in again before adding a passkey.' };
  }
  try {
    const startResp = await cognitoFetch('StartWebAuthnRegistration', { AccessToken: accessToken });
    const startData = await startResp.json();
    if (!startResp.ok || !startData.CredentialCreationOptions) {
      return { success: false, message: startData.message || 'Could not start passkey registration.' };
    }
    let credential: PublicKeyCredential | null;
    try {
      credential = (await navigator.credentials.create(
        decodeCreationOptions(startData.CredentialCreationOptions),
      )) as PublicKeyCredential | null;
    } catch (err) {
      // User cancelled the Face ID / Touch ID prompt, or no authenticator.
      // `cancelled` is a typed flag (W2-02) so the enroll prompt can dismiss
      // quietly on a deliberate dismissal instead of toasting an error.
      const name = err instanceof DOMException ? err.name : '';
      if (name === 'NotAllowedError' || name === 'AbortError') {
        return { success: false, cancelled: true, message: 'Passkey setup was cancelled.' };
      }
      return { success: false, message: 'Your device could not create a passkey. Please try again.' };
    }
    if (!credential) return { success: false, message: 'No passkey was created.' };

    const completeResp = await cognitoFetch('CompleteWebAuthnRegistration', {
      AccessToken: accessToken,
      Credential: encodeRegistration(credential),
    });
    if (!completeResp.ok) {
      const data = await completeResp.json().catch(() => ({}));
      return { success: false, message: data.message || 'Could not finish registering the passkey.' };
    }
    void friendlyName; // Cognito does not take a device name on registration; reserved for UI labelling.
    // W2-02: record WHO enrolled on this device + WHICH credential. The
    // per-user map drives Login's CTA (password-primary on fresh devices,
    // biometric-primary once enrolled) and the Settings "this device" badge.
    // The credential id was previously discarded (audit auth.ts:627 finding).
    try {
      const userEmail = getCurrentUser()?.email || '';
      recordLocalPasskey(userEmail, credential.id);
    } catch { /* storage — non-fatal */ }
    // Legacy device-level boolean kept for back-compat readers
    // (hasEnrolledPlatformPasskey + pre-W2-02 devices that only have this).
    try { localStorage.setItem(PLATFORM_PASSKEY_FLAG, '1'); } catch { /* private mode — non-fatal */ }
    return { success: true, message: `Passkey added. Next time, sign in with ${getBiometricLabel()} — no QR code.` };
  } catch (err) {
    console.error('registerPasskey error:', err);
    return { success: false, message: 'Network error while registering the passkey. Please try again.' };
  }
}

/**
 * Passwordless sign-in with a passkey (B-2). The user enters their email, then
 * authorizes with Face ID / Touch ID / Windows Hello. Because the pool's
 * FactorConfiguration is MULTI_FACTOR_WITH_USER_VERIFICATION, this single step
 * satisfies the required-MFA pool — no separate code. On success, tokens are
 * saved and the caller redirects. `unsupported`/`noPasskey` let the UI fall
 * back to password sign-in cleanly.
 */
export async function signInWithPasskey(email: string): Promise<{
  success: boolean;
  message: string;
  unsupported?: boolean;
  cancelled?: boolean;
  noPasskey?: boolean;
}> {
  if (!isPasskeySupported()) {
    return { success: false, unsupported: true, message: 'This device does not support passkeys.' };
  }
  try {
    const initResp = await cognitoFetch('InitiateAuth', {
      AuthFlow: 'USER_AUTH',
      ClientId: CLIENT_ID,
      AuthParameters: { USERNAME: email, PREFERRED_CHALLENGE: 'WEB_AUTHN' },
    });
    const initData = await initResp.json();
    if (!initResp.ok) {
      return { success: false, message: initData.message || 'Could not start passkey sign-in.' };
    }
    // When the user has no registered passkey (or isn't eligible for the
    // preferred WEB_AUTHN challenge), USER_AUTH returns ChallengeName=
    // 'SELECT_CHALLENGE' with an AvailableChallenges list — NOT a WEB_AUTHN
    // challenge. Treat that as "no passkey, fall back to password" with a typed
    // flag the UI can branch on. Any other unexpected challenge is a generic
    // failure.
    if (initData.ChallengeName === 'SELECT_CHALLENGE') {
      return { success: false, noPasskey: true, message: 'No passkey is registered for this account. Sign in with your password.' };
    }
    if (initData.ChallengeName !== 'WEB_AUTHN' || !initData.ChallengeParameters?.CREDENTIAL_REQUEST_OPTIONS) {
      return { success: false, noPasskey: true, message: 'No passkey is registered for this account. Sign in with your password.' };
    }
    const requestOptions = JSON.parse(initData.ChallengeParameters.CREDENTIAL_REQUEST_OPTIONS);

    // QR-trap fix (2026-06-19): when the user ALSO has a phone/iCloud passkey,
    // Cognito's allowCredentials lists every credential, so Edge/Windows surfaces
    // the cross-device QR even though a local Windows Hello passkey exists. If THIS
    // device recorded a platform passkey for this email, narrow allowCredentials to
    // ONLY it with transports:['internal'] — the browser then engages Windows Hello
    // / Touch ID directly, no QR. (The phone passkey stays usable on the phone.)
    const localCredId = getLocalPasskeyMap()[(email || '').trim().toLowerCase()];
    if (localCredId) {
      requestOptions.allowCredentials = [{ id: localCredId, type: 'public-key', transports: ['internal'] }];
    }

    let assertion: PublicKeyCredential | null;
    try {
      assertion = (await navigator.credentials.get(
        decodeRequestOptions(requestOptions),
      )) as PublicKeyCredential | null;
    } catch (err) {
      const name = err instanceof DOMException ? err.name : '';
      if (name === 'NotAllowedError' || name === 'AbortError') {
        return { success: false, cancelled: true, message: 'Passkey sign-in was cancelled.' };
      }
      return { success: false, message: 'Your device could not complete passkey sign-in.' };
    }
    if (!assertion) return { success: false, message: 'No passkey was selected.' };

    const respResp = await cognitoFetch('RespondToAuthChallenge', {
      ChallengeName: 'WEB_AUTHN',
      ClientId: CLIENT_ID,
      Session: initData.Session,
      ChallengeResponses: {
        USERNAME: email,
        CREDENTIAL: JSON.stringify(encodeAssertion(assertion)),
      },
    });
    const respData = await respResp.json();
    if (respData.AuthenticationResult) {
      const tokens: AuthTokens = {
        accessToken: respData.AuthenticationResult.AccessToken,
        idToken: respData.AuthenticationResult.IdToken,
        refreshToken: respData.AuthenticationResult.RefreshToken,
        expiresAt: Date.now() + expiresInToMs(respData.AuthenticationResult.ExpiresIn),
      };
      saveTokens(tokens);
      return { success: true, message: 'Signed in with passkey' };
    }
    // A further challenge here means the passkey did NOT satisfy MFA in one step
    // (e.g. pool FactorConfiguration not MULTI_FACTOR_WITH_USER_VERIFICATION, or
    // the passkey lacked user verification). Surface a specific message instead
    // of the opaque generic one, and log for triage.
    if (respData.ChallengeName) {
      console.warn('Passkey sign-in returned an unexpected additional challenge:', respData.ChallengeName);
      return { success: false, message: 'Passkey needs additional verification. Please sign in with your password.' };
    }
    return { success: false, message: respData.message || 'Passkey sign-in failed.' };
  } catch (err) {
    console.error('signInWithPasskey error:', err);
    return { success: false, message: 'Network error during passkey sign-in. Please try again.' };
  }
}

// ============================================================
// Passkey management (W2-02 Part 4) — list + delete the signed-in user's
// WebAuthn credentials for the Settings → Security "Your passkeys" card.
//
// Both ops are user-level, browser-direct, ACCESS-token-authorized — the same
// auth model as StartWebAuthnRegistration / GlobalSignOut above (verbatim AWS:
// "Authorize this action with a signed-in user's access token ... Amazon
// Cognito doesn't evaluate IAM policies in requests for this API operation").
// No backend route, no IAM grant, no pool config change.
// ============================================================

/** One row from ListWebAuthnCredentials, normalized for the UI. */
export interface PasskeyCredential {
  credentialId: string;
  /** Cognito-GENERATED name (e.g. "Roaming passkey") — there is no rename API
   *  and no name parameter on registration; may be empty. */
  friendlyName: string;
  /** 'platform' (this-device biometric) | 'cross-platform' (phone / security key). */
  authenticatorAttachment: string;
  authenticatorTransports: string[];
  relyingPartyId: string;
  /** Epoch MILLISECONDS. Cognito's CreatedAt is epoch SECONDS as a float
   *  (e.g. 1736293876.115) — converted here so callers can `new Date(createdAt)`. */
  createdAt: number;
}

/**
 * Pure mapper: raw Cognito WebAuthnCredentialDescription -> PasskeyCredential.
 * Exported for unit tests (shape pinned against the confirmed AWS response,
 * audit §3) — keeps the epoch-seconds conversion testable without fetch.
 */
export function mapWebAuthnCredential(raw: Record<string, unknown>): PasskeyCredential {
  const createdAtSec = typeof raw.CreatedAt === 'number' ? raw.CreatedAt : parseFloat(String(raw.CreatedAt || 0));
  return {
    credentialId: String(raw.CredentialId || ''),
    friendlyName: String(raw.FriendlyCredentialName || ''),
    authenticatorAttachment: String(raw.AuthenticatorAttachment || ''),
    authenticatorTransports: Array.isArray(raw.AuthenticatorTransports)
      ? raw.AuthenticatorTransports.map(String)
      : [],
    relyingPartyId: String(raw.RelyingPartyId || ''),
    createdAt: Number.isFinite(createdAtSec) ? Math.round(createdAtSec * 1000) : 0,
  };
}

/**
 * List ALL of the signed-in user's passkeys (every device, not just this one).
 * Pages on NextToken (MaxResults caps at 20; realistically one page — capped
 * defensively at 5 pages = 100 credentials).
 */
export async function listPasskeys(): Promise<{ ok: boolean; credentials: PasskeyCredential[]; message?: string }> {
  const accessToken = await getAccessTokenRaw();
  if (!accessToken) {
    return { ok: false, credentials: [], message: 'Please sign in again to manage passkeys.' };
  }
  try {
    const credentials: PasskeyCredential[] = [];
    let nextToken: string | undefined;
    for (let page = 0; page < 5; page++) {
      const body: Record<string, unknown> = { AccessToken: accessToken, MaxResults: 20 };
      if (nextToken) body.NextToken = nextToken;
      const resp = await cognitoFetch('ListWebAuthnCredentials', body);
      const data = await resp.json().catch(() => ({} as Record<string, unknown>));
      if (!resp.ok) {
        const errType = String((data as { __type?: string }).__type || '');
        const message = errType.includes('NotAuthorized')
          ? 'Your session has expired. Please sign in again to manage passkeys.'
          : (data as { message?: string }).message || 'Could not load your passkeys.';
        return { ok: false, credentials: [], message };
      }
      const rows = (data as { Credentials?: Record<string, unknown>[] }).Credentials || [];
      for (const row of rows) credentials.push(mapWebAuthnCredential(row));
      nextToken = (data as { NextToken?: string }).NextToken;
      if (!nextToken) break;
    }
    return { ok: true, credentials };
  } catch (err) {
    console.error('listPasskeys error:', err);
    return { ok: false, credentials: [], message: 'Network error loading passkeys. Please try again.' };
  }
}

/**
 * Delete one of the signed-in user's passkeys by credential id.
 * ResourceNotFound is treated as success (already deleted — idempotent).
 * NOTE: deleting a passkey CANNOT lock the user out — password + TOTP remain
 * (pool MFA is required and TOTP is enrolled for all real users). Local
 * per-device state cleanup (clearLocalPasskey) is the CALLER's job, because
 * only the caller knows whether the removed credential was this device's.
 */
export async function deletePasskey(credentialId: string): Promise<{ ok: boolean; message?: string }> {
  const accessToken = await getAccessTokenRaw();
  if (!accessToken) {
    return { ok: false, message: 'Please sign in again to manage passkeys.' };
  }
  try {
    const resp = await cognitoFetch('DeleteWebAuthnCredential', {
      AccessToken: accessToken,
      CredentialId: credentialId,
    });
    if (resp.ok) return { ok: true };
    const data = await resp.json().catch(() => ({} as Record<string, unknown>));
    const errType = String((data as { __type?: string }).__type || '');
    if (errType.includes('ResourceNotFound')) return { ok: true }; // already gone
    return { ok: false, message: (data as { message?: string }).message || 'Could not remove the passkey.' };
  } catch (err) {
    console.error('deletePasskey error:', err);
    return { ok: false, message: 'Network error removing the passkey. Please try again.' };
  }
}

// ============================================================
// Token Refresh
// ============================================================
async function refreshSession(refreshToken: string): Promise<AuthTokens | null> {
  try {
    const response = await cognitoFetch('InitiateAuth', {
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      ClientId: CLIENT_ID,
      AuthParameters: { REFRESH_TOKEN: refreshToken },
    });

    const data = await response.json();

    if (data.AuthenticationResult) {
      const tokens: AuthTokens = {
        accessToken: data.AuthenticationResult.AccessToken,
        idToken: data.AuthenticationResult.IdToken,
        refreshToken: data.AuthenticationResult.RefreshToken || refreshToken, // Use rotated token if provided
        expiresAt: Date.now() + expiresInToMs(data.AuthenticationResult.ExpiresIn),
      };
      saveTokens(tokens);
      return tokens;
    }

    console.warn('Token refresh failed — no AuthenticationResult:', Object.keys(data));
    return null;
  } catch (err) {
    console.error('Token refresh error:', err);
    return null;
  }
}

// ============================================================
// Forgot Password
// ============================================================
export async function forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
  try {
    const response = await cognitoFetch('ForgotPassword', {
      ClientId: CLIENT_ID,
      Username: email,
    });
    const data = await response.json();

    if (data.__type) {
      const msg = data.message || data.Message || 'Failed to send reset code';
      if (data.__type === 'UserNotFoundException') {
        // Don't reveal whether account exists — show generic message
        return { success: true, message: 'If an account exists for this email, a reset code has been sent.' };
      }
      if (data.__type === 'LimitExceededException') {
        return { success: false, message: 'Too many attempts. Please wait before trying again.' };
      }
      return { success: false, message: msg };
    }

    return { success: true, message: 'If an account exists for this email, a reset code has been sent.' };
  } catch (err) {
    return { success: false, message: err instanceof Error ? err.message : 'Network error — please try again' };
  }
}

export async function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<{ success: boolean; message: string }> {
  try {
    const response = await cognitoFetch('ConfirmForgotPassword', {
      ClientId: CLIENT_ID,
      Username: email,
      ConfirmationCode: code,
      Password: newPassword,
    });
    const data = await response.json();

    if (data.__type) {
      const msg = data.message || data.Message || 'Failed to reset password';
      if (data.__type === 'CodeMismatchException') {
        return { success: false, message: 'Invalid or expired code. Please try again.' };
      }
      if (data.__type === 'ExpiredCodeException') {
        return { success: false, message: 'Code has expired. Please request a new one.' };
      }
      if (data.__type === 'InvalidPasswordException') {
        return { success: false, message: msg };
      }
      return { success: false, message: msg };
    }

    return { success: true, message: 'Password reset successfully. You can now sign in.' };
  } catch (err) {
    return { success: false, message: err instanceof Error ? err.message : 'Network error — please try again' };
  }
}

// ============================================================
// Account recovery — "lost passkey AND authenticator (TOTP)" path.
// Public API Gateway routes (/auth/recovery/*), served by cognito-proxy-agent.
// Two proofs required: a one-time email code AND the current password. On
// success the server resets MFA; the next sign-in forces a fresh authenticator.
// ============================================================
export async function requestRecovery(email: string): Promise<{ ok: boolean; message: string }> {
  try {
    const resp = await fetch(`${API_BASE}/auth/recovery/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await resp.json().catch(() => ({}));
    // The endpoint always returns a uniform message (no account enumeration).
    return { ok: data.ok !== false, message: data.message || 'If that email has an account, we sent a code.' };
  } catch {
    return { ok: false, message: 'Could not reach the server. Check your connection and try again.' };
  }
}

export async function verifyRecovery(
  email: string,
  code: string,
  password: string,
): Promise<{ ok: boolean; operatorOnly?: boolean; message: string }> {
  try {
    const resp = await fetch(`${API_BASE}/auth/recovery/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code, password }),
    });
    const data = await resp.json().catch(() => ({}));
    return {
      ok: !!data.ok,
      operatorOnly: !!data.operatorOnly,
      message: data.message || (data.ok ? 'Recovered.' : 'That did not work. Please try again.'),
    };
  } catch {
    return { ok: false, message: 'Could not reach the server. Check your connection and try again.' };
  }
}

// ============================================================
// Sign Out
// ============================================================
// Clears tokens AND runtime caches + service worker so that stale data from the
// previous account cannot bleed into the next signed-in user's view. Prior
// behavior only cleared localStorage tokens, which meant SW-cached API
// responses (credentials, expenses, etc.) would still render briefly on
// page load for the next user before the live fetch completed.
export function signOut() {
  clearTokens();
  // Clear demo-mode flags so the next sign-in doesn't inherit them.
  // Raw localStorage access (not the demo.ts helper) to keep auth.ts
  // independent of the demo module.
  try {
    localStorage.removeItem('nexus_demo_session');
    localStorage.removeItem('nexus_demo_ai_queries');
  } catch { /* storage quota / SSR / private browsing */ }
  // Fire-and-forget; don't block the caller
  void clearAppCaches();
}

/**
 * W2-30 fix (2026-06-11): "Sign out everywhere" — the USER-level Cognito
 * `GlobalSignOut` op revokes every access + refresh token issued to this
 * user across all devices. Browser-direct with the access token via the
 * same cognitoFetch pattern as the TOTP/passkey ceremonies — no backend
 * route needed (the Security page's old button was a disabled stub whose
 * description claimed it worked). Note: ID tokens already in other tabs
 * remain valid until their 1-hour expiry by Cognito design; refresh is
 * what's killed, so every device is forced to re-authenticate within the
 * hour. The caller should follow up with the local signOut().
 */
export async function globalSignOut(): Promise<void> {
  const accessToken = await getAccessTokenRaw();
  if (!accessToken) throw new Error('Not signed in');
  const res = await cognitoFetch('GlobalSignOut', { AccessToken: accessToken });
  if (!res.ok) {
    const data = await res.json().catch(() => ({} as { message?: string }));
    throw new Error((data as { message?: string }).message || 'Global sign-out failed');
  }
}

/**
 * W2-30 (b): compact, human-readable browser/OS description for the
 * Active-sessions card — replaces dumping the raw 80-char user-agent
 * string into the UI. Best-effort sniffing; falls back to "Browser".
 */
export function describeBrowser(ua: string): string {
  const os =
    /Windows NT/.test(ua) ? 'Windows' :
    /iPhone|iPad/.test(ua) ? 'iOS' :
    /Mac OS X/.test(ua) ? 'macOS' :
    /Android/.test(ua) ? 'Android' :
    /Linux/.test(ua) ? 'Linux' : null;
  const browser =
    /Edg\//.test(ua) ? 'Edge' :
    /OPR\//.test(ua) ? 'Opera' :
    /Firefox\//.test(ua) ? 'Firefox' :
    /Chrome\//.test(ua) ? 'Chrome' :
    /Safari\//.test(ua) ? 'Safari' : 'Browser';
  return os ? `${os} · ${browser}` : browser;
}

// ============================================================
// Cache Busting — clear service worker caches on login
// ============================================================
export async function clearAppCaches(): Promise<void> {
  try {
    // Clear all service worker caches
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map(n => caches.delete(n)));
    }
    // Unregister stale service workers so the new one installs fresh
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
    }
  } catch {
    // Non-critical — proceed even if cache clear fails
  }
}

// ============================================================
// MFA Setup (TOTP)
// ============================================================

export async function associateTOTP(session: string): Promise<{ success: boolean; secretCode?: string; session?: string; message: string }> {
  try {
    const response = await cognitoFetch('AssociateSoftwareToken', { Session: session });
    const data = await response.json();

    if (data.SecretCode) {
      return { success: true, secretCode: data.SecretCode, session: data.Session, message: 'Scan the QR code with your authenticator app' };
    }

    return { success: false, message: data.message || 'Failed to generate TOTP secret' };
  } catch (err) {
    console.error('TOTP associate error:', err);
    return { success: false, message: 'Network error during MFA setup' };
  }
}

export async function verifyTOTPSetup(session: string, code: string, friendlyName?: string): Promise<{ success: boolean; session?: string; message: string }> {
  try {
    const response = await cognitoFetch('VerifySoftwareToken', {
      Session: session,
      UserCode: code,
      FriendlyDeviceName: friendlyName || 'Authenticator',
    });

    const data = await response.json();

    if (!response.ok) {
      const errType = data.__type || '';
      console.error('TOTP verify failed:', response.status, errType, data.message);
      if (errType.includes('CodeMismatch') || errType.includes('EnableSoftwareTokenMFA')) {
        return { success: false, message: 'Code mismatch. Delete any old KaspyanNexus entries in your authenticator app, then try a fresh code.' };
      }
      if (errType.includes('NotAuthorized') || errType.includes('InvalidParameter')) {
        return { success: false, message: 'Session expired. Please sign in again.' };
      }
      return { success: false, message: data.message || `Verification failed (${response.status})` };
    }

    if (data.Status === 'SUCCESS') {
      return { success: true, session: data.Session, message: 'MFA enabled successfully' };
    }

    return { success: false, message: data.message || 'Invalid code. Try again.' };
  } catch (err) {
    console.error('TOTP verify error:', err);
    return { success: false, message: 'Verification failed. Please try again.' };
  }
}

export async function completeMFASetup(session: string, email: string): Promise<{ success: boolean; message: string }> {
  try {
    const response = await cognitoFetch('RespondToAuthChallenge', {
      ChallengeName: 'MFA_SETUP',
      ClientId: CLIENT_ID,
      Session: session,
      ChallengeResponses: { USERNAME: email },
    });

    const data = await response.json();

    if (data.AuthenticationResult) {
      const tokens: AuthTokens = {
        accessToken: data.AuthenticationResult.AccessToken,
        idToken: data.AuthenticationResult.IdToken,
        refreshToken: data.AuthenticationResult.RefreshToken,
        expiresAt: Date.now() + expiresInToMs(data.AuthenticationResult.ExpiresIn),
      };
      saveTokens(tokens);
      return { success: true, message: 'MFA setup complete. You are now signed in.' };
    }

    return { success: false, message: data.message || 'MFA setup completion failed' };
  } catch (err) {
    console.error('MFA setup complete error:', err);
    return { success: false, message: 'MFA setup failed' };
  }
}

export function generateTOTPUri(secretCode: string, email: string): string {
  return `otpauth://totp/KaspyanNexus:${encodeURIComponent(email)}?secret=${secretCode}&issuer=KaspyanNexus`;
}

// ============================================================
// OAuth / SSO
// ============================================================
//
// IMPORTANT: No PKCE. iOS Safari's ITP (Intelligent Tracking Prevention)
// clears localStorage during cross-origin redirect chains
// (app → Cognito → Google → Cognito → app), destroying the PKCE verifier.
// Since this is a public client (no client secret), Cognito does NOT
// require PKCE. Security is maintained via:
// - Authorization code grant (code is single-use, short-lived)
// - Redirect URI validation (Cognito only redirects to registered URIs)
// - State parameter stored in cookie (survives ITP, unlike localStorage)
// ============================================================

export type OAuthProvider = 'Google' | 'Microsoft';

/** Generate a random hex string for state parameter */
function generateOAuthState(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Set a short-lived cookie (survives cross-origin redirects, unlike localStorage on iOS Safari).
 * SameSite=Lax allows the cookie to be sent on top-level navigations from external sites.
 */
function setOAuthCookie(name: string, value: string) {
  document.cookie = `${name}=${value}; path=/; max-age=600; SameSite=Lax; Secure`;
}

function getOAuthCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? match[1] : null;
}

function clearOAuthCookie(name: string) {
  document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax; Secure`;
}

/**
 * Start OAuth flow — redirects to Cognito Hosted UI.
 * State parameter stored in cookie for CSRF protection (survives iOS Safari ITP).
 */
export async function startOAuthFlow(provider: OAuthProvider) {
  if (!COGNITO_DOMAIN) {
    console.warn('OAuth not configured — set VITE_COGNITO_DOMAIN');
    return;
  }

  const state = generateOAuthState();

  // Store state in cookie — cookies survive cross-origin redirects on iOS Safari
  // (unlike localStorage which ITP can clear during the redirect chain)
  setOAuthCookie('nexus_oauth_state', state);

  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: REDIRECT_URI,
    identity_provider: provider,
    state,
  });

  window.location.href = `https://${COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`;
}

/**
 * Handle OAuth callback — exchange authorization code for tokens.
 * No PKCE needed (public client). Validates state via cookie.
 */
export async function handleOAuthCallback(code: string, returnedState?: string): Promise<{ success: boolean; message: string }> {
  if (!COGNITO_DOMAIN) {
    return { success: false, message: 'OAuth not configured' };
  }

  // Validate state parameter (CSRF protection) — read from cookie.
  //
  // iOS Safari + audit lesson: the original check was too permissive -
  // `if (savedState && returnedState && savedState !== returnedState)`
  // skipped the CSRF check entirely when EITHER side was missing. That
  // means a cookie-blocked browser (paranoid privacy settings, or ITP
  // mid-flow eviction) silently downgraded CSRF protection. Hardened
  // below to fail-closed in the suspicious cases.
  const savedState = getOAuthCookie('nexus_oauth_state');
  clearOAuthCookie('nexus_oauth_state');
  // Also clean up any legacy localStorage state
  try { localStorage.removeItem('nexus_oauth_state'); localStorage.removeItem('nexus_pkce_verifier'); } catch { /* */ }

  // Both present and mismatched - real CSRF or stale state - abort.
  if (savedState && returnedState && savedState !== returnedState) {
    return { success: false, message: 'OAuth state mismatch - possible CSRF attack. Please try again.' };
  }
  // We saved state but Cognito did not echo it back - abort.
  if (savedState && !returnedState) {
    return { success: false, message: 'OAuth state was not returned by the identity provider. Please try again.' };
  }
  // Cognito echoed state but we have no record - cookie was likely blocked.
  // Continue (the auth code is still single-use + redirect-URI-validated)
  // but log so we can spot a pattern of cookie-blocked sessions.
  if (!savedState && returnedState) {
    console.warn('OAuth: returned state with no saved state - cookie may have been blocked or cleared by ITP. Auth code grant + redirect URI validation still protect this flow.');
  }

  try {
    const tokenParams: Record<string, string> = {
      grant_type: 'authorization_code',
      client_id: CLIENT_ID,
      code,
      redirect_uri: REDIRECT_URI,
    };

    const response = await fetchWithTimeout(
      `https://${COGNITO_DOMAIN}/oauth2/token`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(tokenParams).toString(),
      },
      20000, // 20s timeout for token exchange
    );

    if (!response.ok) {
      const err = await response.text();
      console.error('OAuth token exchange failed:', response.status, err);
      return { success: false, message: `Token exchange HTTP ${response.status}: ${err}` };
    }

    const data = await response.json();

    if (!data.id_token || !data.access_token) {
      console.error('OAuth response missing tokens:', Object.keys(data));
      return { success: false, message: `Response missing tokens. Keys: ${Object.keys(data).join(', ')}` };
    }

    const tokens: AuthTokens = {
      accessToken: data.access_token,
      idToken: data.id_token,
      refreshToken: data.refresh_token,
      expiresAt: Date.now() + expiresInToMs(data.expires_in),
    };

    saveTokens(tokens);
    return { success: true, message: 'Signed in via OAuth' };
  } catch (err) {
    console.error('OAuth callback exception:', err);
    const msg = err instanceof DOMException && err.name === 'AbortError'
      ? 'Token exchange timed out. Please try again.'
      : `OAuth exception: ${err instanceof Error ? err.message : String(err)}`;
    return { success: false, message: msg };
  }
}

/**
 * Save tokens received from the employee-portal activation flow
 * (POST /v1/employee/activate/:token returns Cognito InitiateAuth tokens).
 * Same persistence path as password / OAuth sign-in. S26 Phase 2.
 */
export function saveActivationSession(tokens: { idToken: string; accessToken: string; refreshToken: string; expiresIn?: number }) {
  saveTokens({
    idToken: tokens.idToken,
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    expiresAt: Date.now() + expiresInToMs(tokens.expiresIn),
  });
}

/** Sign out from OAuth (Cognito Hosted UI logout) */
export function oauthSignOut() {
  clearTokens();

  if (COGNITO_DOMAIN) {
    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      logout_uri: window.location.origin + '/login',
    });
    window.location.href = `https://${COGNITO_DOMAIN}/logout?${params.toString()}`;
  }
}

/** Parse the ID token to get user info */
export function getCurrentUser(): AuthUser | null {
  const tokens = loadTokens();
  if (!tokens) return null;

  try {
    const payload = JSON.parse(atob(tokens.idToken.split('.')[1]));
    return {
      cognitoId: payload.sub,
      email: payload.email,
      emailVerified: payload.email_verified,
    };
  } catch {
    return null;
  }
}

// ============================================================
// Test-only exports (Piece B B-7) — NOT part of the public API. The WebAuthn
// base64url codec + JSON<->buffer transforms are module-private; expose them
// under a namespaced object so vitest can verify the encode/decode glue without
// a real navigator.credentials.
// ============================================================
export const __webauthnInternals = {
  b64urlToBuf,
  bufToB64url,
  decodeCreationOptions,
  decodeRequestOptions,
  encodeRegistration,
  encodeAssertion,
};
